/**
 * BUG-002 fix (Phase 2B Sub-dispatch 4) — virtual-row classification helper.
 *
 * Pure function extracted from ReviewTable.tsx so the row-class predicate is
 * unit-testable without mounting the 5000-line ReviewTable component (per the
 * Layer C two-tier discipline, CLAUDE.md 2026-05-28). The class2Banner.ts
 * helper is the precedent for this extraction pattern.
 *
 * THE BUG (diagnostic audits/bug-002-phase1-derived-title-render-2026-05-29.md
 * §L1): the prior predicate required `variants.length === 1 AND no option
 * values` to route a product to the single-line MergedRow. A product with one
 * variant that DID carry an option value (e.g. Fancypants "5oz") flipped the
 * predicate to false, routed to the collapsed ProductRow header (which renders
 * SKU/Price/option/barcode/inventory/weight as hardcoded N/A em dashes), and
 * the merchant saw blanks where extracted data exists.
 *
 * THE FIX: any product with exactly one variant routes to MergedRow, which
 * renders SKU + Price + option1/2/3 + barcode + ... as LIVE editable cells
 * (ReviewTable.tsx MergedRow, lines ~6206-6248). The option-value condition is
 * dropped — single-variant-with-options is a single-line product, not a
 * collapsed multi-variant header. Multi-variant products (>1 variant) keep the
 * ProductRow header + expandable VariantRow behavior unchanged.
 */

export type VirtualRowType = 'merged' | 'product' | 'variant' | 'add-variant';

export interface VirtualRow {
  type: VirtualRowType;
  product: any;
  variants: any[];
  variant?: any;
  isExpanded?: boolean;
  vIdx?: number;
}

/**
 * Flatten the sorted product list into individual <tr> descriptors.
 *
 *   - 1 variant  → one 'merged' row (full data on a single line)
 *   - N variants → one 'product' header row; when expanded, N 'variant' rows
 *                  followed by one 'add-variant' row
 *
 * `expanded` is the Set of product ids the merchant has expanded.
 */
export function buildVirtualRows(
  sortedProducts: any[],
  expanded: Set<string>,
): VirtualRow[] {
  const rows: VirtualRow[] = [];
  for (const product of sortedProducts || []) {
    const variants = product.variants || [];
    const v0 = variants[0];
    // BUG-002 L1 fix: single-variant products route to MergedRow regardless of
    // whether the variant carries option values. (Was: also required no option
    // values, which mis-routed 1-variant-with-options products to ProductRow.)
    const isMerged = variants.length === 1;

    if (isMerged) {
      rows.push({ type: 'merged', product, variant: v0, variants });
    } else {
      const isExpanded = expanded.has(product.id);
      rows.push({ type: 'product', product, variants, isExpanded });
      if (isExpanded) {
        for (let i = 0; i < variants.length; i++) {
          rows.push({ type: 'variant', product, variant: variants[i], variants, vIdx: i });
        }
        rows.push({ type: 'add-variant', product, variants });
      }
    }
  }
  return rows;
}
